import { useEffect, useRef, useState } from 'react';
import { useNavigate, useParams } from 'react-router-dom';
import { Send, CheckCircle2, RotateCcw, Search } from 'lucide-react';
import { toast } from 'sonner';
import api from '../admin/api';

const timeAgo = (ts) => {
  const s = Math.max(0, (Date.now() - new Date(ts)) / 1000);
  if (s < 60) return 'just now';
  if (s < 3600) return `${Math.floor(s / 60)}m ago`;
  if (s < 86400) return `${Math.floor(s / 3600)}h ago`;
  return `${Math.floor(s / 86400)}d ago`;
};

const ChatInboxPage = () => {
  const { cid } = useParams();
  const navigate = useNavigate();
  const [convos, setConvos] = useState([]);
  const [messages, setMessages] = useState([]);
  const [draft, setDraft] = useState('');
  const [search, setSearch] = useState('');
  const [filter, setFilter] = useState('active');
  const bottomRef = useRef(null);
  const active = convos.find((c) => c.id === cid);

  const loadConvos = async () => {
    try {
      const { data } = await api.get('/admin/chats');
      setConvos(data);
    } catch (e) { /* silent */ }
  };

  const loadMessages = async () => {
    if (!cid) return;
    try {
      const { data } = await api.get(`/admin/chats/${cid}/messages`);
      setMessages(data);
    } catch (e) { /* silent */ }
  };

  useEffect(() => {
    loadConvos();
    const t = setInterval(loadConvos, 5000);
    return () => clearInterval(t);
  }, []);

  useEffect(() => {
    loadMessages();
    const t = setInterval(loadMessages, 4000);
    return () => clearInterval(t);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [cid]);

  useEffect(() => {
    bottomRef.current?.scrollIntoView({ behavior: 'smooth' });
  }, [messages.length]);

  const send = async (e) => {
    e.preventDefault();
    const text = draft.trim();
    if (!text || !cid) return;
    setDraft('');
    const { data } = await api.post(`/admin/chats/${cid}/messages`, { text });
    setMessages((m) => [...m, data]);
  };

  const setStatus = async (status) => {
    await api.patch(`/admin/chats/${cid}`, { status });
    toast.success(status === 'resolved' ? 'Conversation resolved' : 'Conversation reopened');
    loadConvos();
  };

  const filtered = convos.filter((c) =>
    (filter === 'all' || c.status === filter) &&
    (!search || c.visitor_name.toLowerCase().includes(search.toLowerCase()) || (c.last_message || '').toLowerCase().includes(search.toLowerCase()))
  );

  return (
    <div data-testid="admin-chat" className="space-y-6">
      <h1 className="font-serif text-3xl font-medium text-ink">Live Chat</h1>
      <div className="grid gap-6 lg:grid-cols-[340px_1fr]" style={{ height: 'calc(100vh - 220px)', minHeight: 480 }}>
        <div className="flex flex-col overflow-hidden border border-line bg-paper">
          <div className="space-y-3 border-b border-line p-4">
            <div className="relative">
              <Search className="absolute left-3 top-1/2 h-4 w-4 -translate-y-1/2 text-muted2" />
              <input data-testid="chat-search" value={search} onChange={(e) => setSearch(e.target.value)} placeholder="Search conversations…" className="input-field !py-2.5 !pl-9 !text-sm" />
            </div>
            <div className="flex gap-2">
              {['active', 'resolved', 'all'].map((f) => (
                <button key={f} data-testid={`chat-filter-${f}`} onClick={() => setFilter(f)}
                  className={`flex-1 border px-3 py-1.5 text-xs font-semibold capitalize transition-colors ${filter === f ? 'border-bronze bg-bronze text-paper' : 'border-line text-muted2'}`}>
                  {f}
                </button>
              ))}
            </div>
          </div>
          <div className="flex-1 divide-y divide-line overflow-y-auto">
            {filtered.map((c) => (
              <button key={c.id} data-testid={`chat-item-${c.id.slice(0, 8)}`} onClick={() => navigate(`/admin/chat/${c.id}`)}
                className={`block w-full px-4 py-3.5 text-left transition-colors ${cid === c.id ? 'bg-paper2' : 'hover:bg-paper2/50'}`}>
                <div className="flex items-center justify-between gap-2">
                  <span className="flex items-center gap-2 font-medium text-ink">
                    {c.visitor_name}
                    {(c.unread_admin || 0) > 0 && <span className="flex h-5 min-w-5 items-center justify-center rounded-full bg-bronze px-1.5 text-[10px] font-bold text-paper">{c.unread_admin}</span>}
                  </span>
                  <span className="text-[11px] text-muted2">{timeAgo(c.last_activity)}</span>
                </div>
                <p className="mt-1 truncate text-xs text-muted2">{c.last_message || 'No messages yet'}</p>
                <p className="mt-1 text-[11px] text-muted2/70">
                  {c.location || 'Unknown location'} · {c.device || '—'} · {c.status === 'resolved' ? 'Resolved' : `on ${c.current_page || '—'}`}
                </p>
              </button>
            ))}
            {filtered.length === 0 && <p className="px-4 py-10 text-center text-sm text-muted2">No conversations.</p>}
          </div>
        </div>

        <div className="flex flex-col overflow-hidden border border-line bg-paper">
          {!active ? (
            <div className="flex flex-1 items-center justify-center text-sm text-muted2">Select a conversation to view the chat.</div>
          ) : (
            <>
              <div className="flex flex-wrap items-center justify-between gap-3 border-b border-line px-5 py-4">
                <div>
                  <p className="font-medium text-ink">{active.visitor_name}</p>
                  <p className="text-xs text-muted2">
                    {active.location || 'Unknown location'} · started {new Date(active.created_at).toLocaleString()} · on <span className="font-medium">{active.current_page || '—'}</span>
                  </p>
                </div>
                {active.status === 'active' ? (
                  <button data-testid="chat-resolve-button" onClick={() => setStatus('resolved')} className="inline-flex items-center gap-2 border border-line px-4 py-2 text-xs font-semibold text-ink transition-colors hover:border-bronze hover:text-bronze">
                    <CheckCircle2 className="h-4 w-4" /> Mark Resolved
                  </button>
                ) : (
                  <button data-testid="chat-reopen-button" onClick={() => setStatus('active')} className="inline-flex items-center gap-2 border border-line px-4 py-2 text-xs font-semibold text-ink transition-colors hover:border-bronze hover:text-bronze">
                    <RotateCcw className="h-4 w-4" /> Reopen
                  </button>
                )}
              </div>
              <div data-testid="admin-chat-messages" className="flex-1 space-y-3 overflow-y-auto bg-paper2/40 p-5">
                {messages.map((m) => (
                  <div key={m.id} className={`flex ${m.sender === 'admin' ? 'justify-end' : 'justify-start'}`}>
                    <div className={`max-w-[75%] rounded-lg px-3.5 py-2.5 text-sm leading-relaxed ${m.sender === 'admin' ? 'bg-bronze text-paper' : 'border border-line bg-paper text-ink'}`}>
                      <p>{m.text}</p>
                      <p className={`mt-1 text-[10px] ${m.sender === 'admin' ? 'text-paper/60' : 'text-muted2/70'}`}>{timeAgo(m.ts)}</p>
                    </div>
                  </div>
                ))}
                {messages.length === 0 && <p className="py-10 text-center text-sm text-muted2">No messages yet.</p>}
                <div ref={bottomRef} />
              </div>
              <form onSubmit={send} className="flex items-center gap-3 border-t border-line p-4">
                <input data-testid="admin-chat-input" value={draft} onChange={(e) => setDraft(e.target.value)} placeholder="Reply to visitor…" className="input-field" />
                <button type="submit" data-testid="admin-chat-send" disabled={!draft.trim()} className="btn-primary !px-5 !py-3.5 disabled:opacity-40">
                  <Send className="h-4 w-4" />
                </button>
              </form>
            </>
          )}
        </div>
      </div>
    </div>
  );
};

export default ChatInboxPage;
